import React from 'react';
import { useEffect, useState } from 'react';
import { Route, Routes, useLocation } from 'react-router-dom';
import PropTypes from 'prop-types';
import Utils from '../../utils/utils';
import i18n from '../../i18n';
import ProgressBar from '../signup-funnel/ProgressBar';
import useCollectionDialogs from '../../hooks/useCollectionDialogs';
import CollectionDialogs from './dialogs/CollectionDialogs';
import { STEPS } from '../../constants/applicationFunnel';
import Api from '../../api/Api';

import Step1Collection from './steps/Step1Collection';
import Step2Collection from './steps/Step2Collection';
import Step3Collection from './steps/Step3Collection';
import Step4Collection from './steps/Step4Collection';
import Step5Collection from './steps/Step5Collection';
import Step6Collection from './steps/Step6Collection';
import Step7Collection from './steps/Step7Collection';
import { useDevice } from '@homeexchange/shared';

const steps = [
    Step1Collection,
    Step2Collection,
    Step3Collection,
    Step4Collection,
    Step5Collection,
    Step6Collection,
    Step7Collection
];

const TOTAL_STEPS = steps.length;

const getCurrentStep = (pathname) => {
    const match = pathname.match(/step(\d+)$/i);
    if (!match) return 0;
    const step = Number(match[1] || 0);
    return STEPS.includes(`step${step}`) ? step : 0;
};

const getLocalUserPath = () => {
    const lang = (document?.documentElement?.lang || '').toLowerCase();
    return ['fr', 'en', 'it'].includes(lang) ? '' : `/${lang}`;
};

const stripTrailingSlash = (p) => p.replace(/\/+$/, '');

const logoButtonStyle = { background: 'transparent', border: 0, padding: 0, cursor: 'pointer' };

const CollectionHeader = ({ onExitClick, currentStep, showExitButton }) => {
    const { isMobile } = useDevice();

    const logo = isMobile ? 'logo-symbol-collection.svg' : 'logo-integral-collection.svg';

    return (
        <div className="header-signup-collection">
            <div className="header-signup-buttons">
                <button
                    type="button"
                    className="logo-button"
                    onClick={onExitClick}
                    aria-label={i18n.t('common:exit')}
                    style={logoButtonStyle}
                >
                    <img
                        src={`${Utils.getAssetsDomain()}/images/website/logo/${logo}`}
                        alt="HomeExchange Collection"
                    />
                </button>
                {showExitButton && (
                    <button className="btn btn-exit" onClick={onExitClick} type="button">
                        {i18n.t('common:exit')}
                    </button>
                )}
            </div>
            {currentStep > 0 && <ProgressBar currentStep={currentStep} totalSteps={TOTAL_STEPS} />}
        </div>
    );
};

CollectionHeader.propTypes = {
    onExitClick: PropTypes.func.isRequired,
    currentStep: PropTypes.number.isRequired,
    showExitButton: PropTypes.bool.isRequired
};

const StepRoutes = () => (
    <Routes>
        {steps.map((Cmp, idx) => {
            const path = `step${idx + 1}`;
            return <Route key={path} path={path} element={<Cmp />} />;
        })}
    </Routes>
);

const HeaderSignupCollection = () => {
    const { pathname } = useLocation();
    const localUser = getLocalUserPath();
    const currentStep = getCurrentStep(pathname);
    const normalizedPath = stripTrailingSlash(pathname);

    const noDialogPaths = [
        `${localUser}/collection/propose-my-home`,
        `${localUser}/collection/propose-my-home/step1`,
        `${localUser}/collection/propose-my-home/step2`,
        `${localUser}/collection/propose-my-home/step3`
    ].map(stripTrailingSlash);

    const shouldShowDialogs = !noDialogPaths.includes(normalizedPath);
    const { dialogs, handlers, actions } = useCollectionDialogs(shouldShowDialogs);
    const showExitButton = pathname !== `${localUser}/collection/propose-my-home`;
    const [shouldDisplayModal, setShouldDisplayModal] = useState(false);

    useEffect(() => {
        const getData = async () => {
            try {
                const data = await Api.Home.getHomeModeration();
                const canDelete = Boolean(data?.home_moderation?.to_recatch);
                setShouldDisplayModal(canDelete);
            } catch (error) {
                setShouldDisplayModal(false);
                console.error('Error fetching user data:', error);
            }
        };

        getData();
    }, []);

    return (
        <>
            <CollectionHeader
                onExitClick={actions.openPause}
                currentStep={currentStep}
                showExitButton={showExitButton}
            />

            <StepRoutes />
            <CollectionDialogs
                dialogs={dialogs}
                handlers={handlers}
                actions={actions}
                canDeleteApplication={shouldDisplayModal}
            />
        </>
    );
};

export default HeaderSignupCollection;
